import { BRAND } from '../brand';
import { APP_TIME_ZONE } from '../constants';
import type { RegistrantType } from '../models';
import { formatPhoneForDisplay } from '../phone';
import { REGISTRANT_TYPE_LABELS } from '../registrants';

/**
 * תבנית דוח ה-PDF הממותגת לעבודה.
 *
 * הפונקציה טהורה: מקבלת נתונים ומחזירה מסמך HTML מלא (RTL, A4) שמוכן להמרה ל-PDF.
 * בשלב 4 ה-Worker ישלוף את הנתונים העדכניים ביותר ברגע ההורדה, ימלא את התבנית
 * וימיר אותה ל-PDF — כך הדוח תמיד תואם את המצב הנוכחי וכולל עברית תקינה.
 */

export interface JobReportRow {
  /** קבלן (מביא כמות עובדים) או עובד עצמאי (עובד אחד) */
  registrantType: RegistrantType;
  name: string;
  /** E.164 */
  phone: string;
  workerCount: number;
}

export interface JobReportInput {
  job: {
    name: string;
    /** YYYY-MM-DD */
    date: string;
    /** HH:mm */
    reportTime: string;
    location: string;
  };
  rows: JobReportRow[];
  generatedAt: Date;
  /** הלוגו המלא — data URI (מומלץ, כדי שה-PDF יהיה עצמאי) או כתובת מלאה */
  logoSrc: string;
  /** הטמעת גופן (@font-face) — אופציונלי */
  fontCss?: string;
  /** סימון "דוגמה" על הדף, לתצוגות מקדימות בלבד */
  sampleWatermark?: boolean;
}

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function formatReportJobDate(date: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return date;
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const long = new Intl.DateTimeFormat('he-IL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }).format(d);
  return `${long} (${m[3]}.${m[2]}.${m[1]})`;
}

export function formatReportTimestamp(at: Date): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: APP_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(at);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';
  return `${get('day')}.${get('month')}.${get('year')} ${get('hour')}:${get('minute')}`;
}

/** קבלנים קודם, אחריהם עובדים עצמאיים; בתוך כל קבוצה — לפי שם. */
export function sortReportRows(rows: JobReportRow[]): JobReportRow[] {
  const order: Record<RegistrantType, number> = { contractor: 0, worker: 1 };
  return [...rows].sort(
    (a, b) => order[a.registrantType] - order[b.registrantType] || a.name.localeCompare(b.name, 'he'),
  );
}

export function reportBreakdown(rows: JobReportRow[]) {
  const contractors = rows.filter((r) => r.registrantType === 'contractor');
  const workers = rows.filter((r) => r.registrantType === 'worker');
  return {
    contractorCount: contractors.length,
    contractorWorkers: reportTotal(contractors),
    individualWorkers: reportTotal(workers),
    total: reportTotal(rows),
  };
}

export function reportTotal(rows: JobReportRow[]): number {
  return rows.reduce((sum, r) => sum + (Number.isFinite(r.workerCount) ? r.workerCount : 0), 0);
}

export function renderJobReportHtml(input: JobReportInput): string {
  const c = BRAND.colors;
  const rows = sortReportRows(input.rows);
  const breakdown = reportBreakdown(rows);
  const total = breakdown.total;
  const generated = formatReportTimestamp(input.generatedAt);
  const e = escapeHtml;

  const bodyRows = rows.length
    ? rows
        .map(
          (r, i) => `
        <tr>
          <td class="num">${i + 1}</td>
          <td>${e(r.name)}</td>
          <td><span class="type type-${r.registrantType}">${REGISTRANT_TYPE_LABELS[r.registrantType]}</span></td>
          <td class="ltr">${e(formatPhoneForDisplay(r.phone))}</td>
          <td class="count">${r.workerCount}</td>
        </tr>`,
        )
        .join('')
    : `<tr><td colspan="5" class="empty">אין עדיין נרשמים לעבודה זו</td></tr>`;

  return `<!doctype html>
<html lang="he" dir="rtl">
<head>
<meta charset="utf-8" />
<title>${e(BRAND.name)} — ${e(input.job.name)}</title>
<style>
${input.fontCss ?? ''}
@page { size: A4; margin: 14mm 14mm 16mm; }
body { padding-bottom: 12mm; }
* { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body {
  font-family: 'Heebo', 'Arial Hebrew', Arial, sans-serif;
  color: ${c.navyDeep};
  font-size: 11pt;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.ltr { direction: ltr; unicode-bidi: isolate; text-align: right; }
header.brand {
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
  padding-bottom: 10px;
  border-bottom: 3px solid ${c.red};
}
header.brand img { height: 118px; }
header.brand .title { text-align: left; }
header.brand .title h1 { margin: 0; font-size: 18pt; font-weight: 800; color: ${c.navy}; }
header.brand .title p { margin: 4px 0 0; font-size: 9.5pt; color: ${c.muted}; }
.job {
  margin: 16px 0 14px;
  display: grid; grid-template-columns: 1fr 1fr; gap: 0;
  border: 1px solid #d5dade; border-radius: 6px; overflow: hidden;
}
.job div { padding: 9px 12px; border-bottom: 1px solid #e3e7ea; }
.job div:nth-child(odd) { border-left: 1px solid #e3e7ea; }
.job div:nth-last-child(-n+2) { border-bottom: 0; }
.job dt { font-size: 8.5pt; color: ${c.muted}; margin: 0 0 2px; }
.job dd { margin: 0; font-size: 12pt; font-weight: 600; }
.job .name { grid-column: 1 / -1; background: ${c.navy}; color: #fff; border-left: 0 !important; }
.job .name dt { color: ${c.steel}; }
.job .name dd { font-size: 15pt; font-weight: 800; }
table { width: 100%; border-collapse: collapse; }
thead { display: table-header-group; }
th {
  background: ${c.navy}; color: #fff; font-weight: 600; font-size: 10pt;
  text-align: right; padding: 8px 10px;
}
td { padding: 7px 10px; border-bottom: 1px solid #e3e7ea; }
tbody tr:nth-child(even) td { background: #f5f7f8; }
tr { page-break-inside: avoid; }
.num { width: 34px; color: ${c.muted}; }
.count { width: 110px; font-weight: 700; text-align: center; font-variant-numeric: tabular-nums; }
th.count { text-align: center; }
.empty { text-align: center; color: ${c.muted}; padding: 18px; }
.type { display: inline-block; padding: 1px 8px; border-radius: 4px; font-size: 9pt; font-weight: 600; }
.type-contractor { background: ${c.navy}; color: #fff; }
.type-worker { background: #e6e9ec; color: ${c.navyDeep}; }
.summary { margin: 10px 0 0; display: flex; gap: 18px; font-size: 10pt; color: ${c.muted}; }
.summary b { color: ${c.navyDeep}; font-variant-numeric: tabular-nums; }
tfoot td {
  background: ${c.navyDeep}; color: #fff; font-weight: 800; font-size: 12pt;
  border-top: 3px solid ${c.red}; border-bottom: 0;
}
tfoot .count { font-size: 15pt; }
footer.brand {
  position: fixed; bottom: 0; left: 0; right: 0; background: #fff;
  display: flex; justify-content: space-between;
  font-size: 8.5pt; color: ${c.muted};
  border-top: 1px solid #d5dade; padding-top: 5px;
}
.watermark {
  position: fixed; top: 42%; left: 0; right: 0; text-align: center;
  font-size: 64pt; font-weight: 800; color: rgba(185, 37, 40, 0.10);
  transform: rotate(-24deg);
}
</style>
</head>
<body>
${input.sampleWatermark ? '<div class="watermark">דוגמה</div>' : ''}
<header class="brand">
  <img src="${e(input.logoSrc)}" alt="${e(BRAND.name)}" />
  <div class="title">
    <h1>דוח עובדים לעבודה</h1>
    <p>הופק: <span class="ltr">${generated}</span></p>
  </div>
</header>

<dl class="job">
  <div class="name"><dt>שם העבודה</dt><dd>${e(input.job.name)}</dd></div>
  <div><dt>תאריך</dt><dd>${e(formatReportJobDate(input.job.date))}</dd></div>
  <div><dt>שעת התייצבות</dt><dd class="ltr">${e(input.job.reportTime)}</dd></div>
  <div style="grid-column: 1 / -1"><dt>מיקום</dt><dd>${e(input.job.location)}</dd></div>
</dl>

<table>
  <thead>
    <tr><th class="num">#</th><th>שם</th><th>סוג</th><th>טלפון</th><th class="count">כמות עובדים</th></tr>
  </thead>
  <tbody>${bodyRows}
  </tbody>
  <tfoot>
    <tr><td colspan="4">סה״כ עובדים</td><td class="count">${total}</td></tr>
  </tfoot>
</table>
<p class="summary">
  <span>עובדי קבלנים: <b>${breakdown.contractorWorkers}</b> (${breakdown.contractorCount} קבלנים)</span>
  <span>עובדים עצמאיים: <b>${breakdown.individualWorkers}</b></span>
</p>

<footer class="brand">
  <span>${e(BRAND.pdf.footer)}</span>
  <span>הופק: <span class="ltr">${generated}</span></span>
</footer>
</body>
</html>`;
}
